import { Real } from "@siteimprove/alfa-math";
import { Parser } from "@siteimprove/alfa-parser";
import { Function, Token, } from "../../syntax/index.js";
import { Keyword } from "../textual/keyword.js";
import { Angle, Number, Percentage } from "../numeric/index.js";
import { Format } from "./format.js";
import { RGB } from "./rgb.js";
const { pair, map, either, option, right, take, delimited } = Parser;
/**
 * @public
 */
export class HSL extends Format {
    static of(hue, saturation, lightness, alpha) {
        return new HSL(hue.resolve(), saturation.resolve(), lightness.resolve(), alpha.resolve());
    }
    _hue;
    _saturation;
    _lightness;
    _red;
    _green;
    _blue;
    _alpha;
    constructor(hue, saturation, lightness, alpha) {
        super("hsl");
        this._hue = hue;
        this._saturation = saturation;
        this._lightness = lightness;
        this._alpha = alpha;
        const degrees = Angle.isAngle(hue) ? hue.withUnit("deg").value : hue.value;
        const [red, green, blue] = hslToRgb(Real.modulo(degrees, 360) / 60, Real.clamp(saturation.value, 0, 1), Real.clamp(lightness.value, 0, 1));
        this._red = Percentage.of(red);
        this._green = Percentage.of(green);
        this._blue = Percentage.of(blue);
    }
    get hue() {
        return this._hue;
    }
    get saturation() {
        return this._saturation;
    }
    get lightness() {
        return this._lightness;
    }
    get red() {
        return this._red;
    }
    get green() {
        return this._green;
    }
    get blue() {
        return this._blue;
    }
    get alpha() {
        return this._alpha;
    }
    resolve() {
        return RGB.of(...Format.resolve(this.red, this.green, this.blue, this.alpha));
    }
    equals(value) {
        return (value instanceof HSL &&
            value._hue.equals(this._hue) &&
            value._saturation.equals(this._saturation) &&
            value._lightness.equals(this._lightness) &&
            value._alpha.equals(this._alpha));
    }
    hash(hash) {
        hash
            .writeHashable(this._hue)
            .writeHashable(this._saturation)
            .writeHashable(this._lightness)
            .writeHashable(this._alpha);
    }
    toJSON() {
        return {
            ...super.toJSON(),
            hue: this._hue.toJSON(),
            saturation: this._saturation.toJSON(),
            lightness: this._lightness.toJSON(),
            alpha: this._alpha.toJSON(),
        };
    }
    toString() {
        return `hsl(${this._hue} ${this._saturation} ${this._lightness}${this._alpha.value === 1 ? "" : ` / ${this._alpha}`})`;
    }
}
/**
 * @public
 */
(function (HSL) {
    function isHSL(value) {
        return value instanceof HSL;
    }
    HSL.isHSL = isHSL;
    /**
     * {@link https://drafts.csswg.org/css-color/#typedef-alpha-value}
     */
    const parseAlpha = either(Number.parse, (Percentage.parse));
    /**
     * {@link https://drafts.csswg.org/css-color/#typedef-hue}
     */
    const parseHue = either(Number.parse, Angle.parse);
    const orNone = (parser) => either(parser, map(Keyword.parse("none"), () => Percentage.of(0)));
    const parseTriplet = (separator, acceptNone = false) => map(pair(acceptNone
        ? either(parseHue, map(Keyword.parse("none"), () => Number.of(0)))
        : parseHue, take(right(separator, acceptNone
        ? orNone((Percentage.parse))
        : Percentage.parse), 2)), ([h, [s, l]]) => [h, s, l]);
    /**
     * @remarks
     * Modern syntax is supposed to accept numbers in addition to percentages
     * for saturation and lightness. This seems to have poor browser support
     * currently, so we ignore it until we encounter it in the wild.
     * Look at what is done for RGB parser if need be.
     */
    const parseModern = pair(parseTriplet(option(Token.parseWhitespace), true), option(right(delimited(option(Token.parseWhitespace), Token.parseDelim("/")), orNone(parseAlpha))));
    const parseLegacy = pair(parseTriplet(delimited(option(Token.parseWhitespace), Token.parseComma)), option(right(delimited(option(Token.parseWhitespace), Token.parseComma), parseAlpha)));
    /**
     * {@link https://drafts.csswg.org/css-color/#funcdef-hsl}
     */
    HSL.parse = map(Function.parse((fn) => fn.value === "hsl" || fn.value === "hsla", either(parseLegacy, parseModern)), (result) => {
        const [, [[hue, saturation, lightness], alpha]] = result;
        return HSL.of(hue, saturation, lightness, alpha.getOrElse(() => Number.of(1)));
    });
})(HSL || (HSL = {}));
/**
 * {@link https://drafts.csswg.org/css-color/#hsl-to-rgb}
 */
function hslToRgb(hue, saturation, lightness) {
    const t2 = lightness <= 0.5
        ? lightness * (saturation + 1)
        : lightness + saturation - lightness * saturation;
    const t1 = lightness * 2 - t2;
    return [
        hueToRgb(t1, t2, Real.modulo(hue + 2, 6)),
        hueToRgb(t1, t2, hue),
        hueToRgb(t1, t2, Real.modulo(hue - 2, 6)),
    ];
}
/**
 * {@link https://drafts.csswg.org/css-color/#hsl-to-rgb}
 */
function hueToRgb(t1, t2, hue) {
    if (hue < 1) {
        return t1 + (t2 - t1) * hue;
    }
    if (hue < 3) {
        return t2;
    }
    if (hue < 4) {
        return t1 + (t2 - t1) * (4 - hue);
    }
    return t1;
}
//# sourceMappingURL=hsl.js.map